/**
 * useFeedback Hook
 *
 * React hook for managing feedback requests
 */

import { useState, useEffect, useCallback } from 'react';
import { createComponentLogger } from '@/utils/logger';
import type {
  FeedbackRequest,
  FeedbackRequestWithDetails,
  FeedbackFilters,
  FeedbackMetrics,
  FeedbackCommentWithUser,
  FeedbackStatusHistory
} from '@/types/feedback';
import * as feedbackService from '@/services/feedbackService';

const log = createComponentLogger('useFeedback', 'hooks');

// =====================================================
// Types
// =====================================================

interface UseFeedbackListReturn {
  data: FeedbackRequestWithDetails[];
  count: number;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  setFilters: (filters: FeedbackFilters) => void;
  filters: FeedbackFilters;
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
}

interface UseMyFeedbackReturn {
  data: FeedbackRequest[];
  count: number;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface UseFeedbackDetailReturn {
  data: FeedbackRequestWithDetails | null;
  comments: FeedbackCommentWithUser[];
  statusHistory: FeedbackStatusHistory[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  refetchComments: () => Promise<void>;
}

interface UseFeedbackMetricsReturn {
  data: FeedbackMetrics | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// =====================================================
// Hooks
// =====================================================

/**
 * Hook for fetching paginated feedback list (admin view)
 */
export function useFeedbackList(initialFilters: FeedbackFilters = {}): UseFeedbackListReturn {
  const [data, setData] = useState<FeedbackRequestWithDetails[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<FeedbackFilters>(initialFilters);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const offset = (page - 1) * pageSize;
      const result = await feedbackService.getFeedbackList(filters, pageSize, offset);
      setData(result.data);
      setCount(result.count);
    } catch (err) {
      log.error('Failed to fetch feedback list', {}, err as Error);
      setError(err instanceof Error ? err.message : 'Failed to fetch feedback');
    } finally {
      setLoading(false);
    }
  }, [filters, page, pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [filters]);

  return {
    data,
    count,
    loading,
    error,
    refetch: fetchData,
    setFilters,
    filters,
    page,
    setPage,
    pageSize,
    setPageSize
  };
}

/**
 * Hook for fetching current user's feedback
 */
export function useMyFeedback(): UseMyFeedbackReturn {
  const [data, setData] = useState<FeedbackRequest[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await feedbackService.getMyFeedback();
      setData(result.data);
      setCount(result.count);
    } catch (err) {
      log.error('Failed to fetch user feedback', {}, err as Error);
      setError(err instanceof Error ? err.message : 'Failed to fetch feedback');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    count,
    loading,
    error,
    refetch: fetchData
  };
}

/**
 * Hook for fetching a single feedback request with details
 */
export function useFeedbackDetail(id: string | null): UseFeedbackDetailReturn {
  const [data, setData] = useState<FeedbackRequestWithDetails | null>(null);
  const [comments, setComments] = useState<FeedbackCommentWithUser[]>([]);
  const [statusHistory, setStatusHistory] = useState<FeedbackStatusHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!id) {
      setData(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const [feedbackData, commentsData, historyData] = await Promise.all([
        feedbackService.getFeedbackById(id),
        feedbackService.getComments(id),
        feedbackService.getStatusHistory(id)
      ]);

      setData(feedbackData);
      setComments(commentsData);
      setStatusHistory(historyData);
    } catch (err) {
      log.error('Failed to fetch feedback detail', { id }, err as Error);
      setError(err instanceof Error ? err.message : 'Failed to fetch feedback');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const refetchComments = useCallback(async () => {
    if (!id) {
return;
}

    try {
      const commentsData = await feedbackService.getComments(id);
      setComments(commentsData);
    } catch (err) {
      log.error('Failed to refetch comments', { id }, err as Error);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    comments,
    statusHistory,
    loading,
    error,
    refetch: fetchData,
    refetchComments
  };
}

/**
 * Hook for fetching feedback metrics
 */
export function useFeedbackMetrics(): UseFeedbackMetricsReturn {
  const [data, setData] = useState<FeedbackMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const metrics = await feedbackService.getFeedbackMetrics();
      setData(metrics);
    } catch (err) {
      log.error('Failed to fetch feedback metrics', {}, err as Error);
      setError(err instanceof Error ? err.message : 'Failed to fetch metrics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    refetch: fetchData
  };
}

/**
 * Hook for fetching admin users for assignment
 */
export function useAdminUsers() {
  const [data, setData] = useState<Array<{ id: string; email: string; full_name: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const users = await feedbackService.getAdminUsers();
        setData(users);
      } catch (err) {
        log.error('Failed to fetch admin users', {}, err as Error);
        setError(err instanceof Error ? err.message : 'Failed to fetch admin users');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return { data, loading, error };
}
